// Admin > Analytics: where questions come from, on a map of the US (state shapes from us-atlas,
// already projected to Albers USA, so Alaska and Hawaii sit inset at the bottom left) with the
// southern band of Canada above it (canada-shape.ts, same projection). The more questions, the stronger the colour. Counts only: the map never links a
// place to its questions. The shapes (about 100 KB) load only when this page opens.

import { useEffect, useState } from "react";
import { US_STATES } from "@ask-hr/core/sources";
import type { GeometryCollection, Topology } from "topojson-specification";

interface Shape {
  code: string;
  name: string;
  d: string;
  label: [number, number] | null; // centroid, for states big enough to hold a number
}

const CODE_BY_NAME = new Map(Object.entries(US_STATES).map(([code, name]) => [name, code]));

interface Shapes {
  states: Shape[];
  canada: { d: string; label: [number, number]; top: number };
}

async function loadShapes(): Promise<Shapes> {
  const [{ default: topo }, { feature }, { geoPath }, canada] = await Promise.all([
    import("us-atlas/states-albers-10m.json"),
    import("topojson-client"),
    import("d3-geo"),
    import("./canada-shape.js"),
  ]);
  const path = geoPath();
  const t = topo as unknown as Topology<{ states: GeometryCollection<{ name: string }> }>;
  const states = feature(t, t.objects.states).features.flatMap((f) => {
    const name = f.properties.name;
    const code = CODE_BY_NAME.get(name);
    const d = path(f);
    if (!code || !d) return [];
    const [x, y] = path.centroid(f);
    return [{ code, name, d, label: path.area(f) > 1800 ? ([x, y] as [number, number]) : null }];
  });
  return { states, canada: { d: canada.CANADA_PATH, label: canada.CANADA_LABEL, top: canada.CANADA_TOP } };
}

const plural = (n: number) => `${n} ${n === 1 ? "question" : "questions"}`;

export function UsageMap({ places }: { places: Record<string, number> }) {
  const [shapes, setShapes] = useState<Shapes | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    loadShapes().then(
      (s) => live && setShapes(s),
      () => live && setFailed(true),
    );
    return () => {
      live = false;
    };
  }, []);

  const count = (k: string) => places[k] ?? 0;
  const keys = [...Object.keys(US_STATES).map((c) => `US-${c}`), "CANADA"];
  const max = Math.max(1, ...keys.map(count));
  // Square root, so one busy state doesn't wash every other one out to nothing.
  const share = (n: number) => (n ? 0.15 + 0.85 * Math.sqrt(n / max) : 0);
  const fill = (n: number) => (n ? `color-mix(in srgb, var(--accent) ${Math.round(share(n) * 100)}%, var(--bg))` : "var(--map-empty)");
  const strong = (n: number) => share(n) > 0.55;

  const ranked = keys
    .filter((k) => count(k))
    .sort((a, b) => count(b) - count(a))
    .map((k) => `${k === "CANADA" ? "Canada" : (US_STATES[k.slice(3)] ?? k)}: ${count(k)}`);
  const notRecorded = count("US");
  const unknown = count("unknown");
  const canada = count("CANADA");

  return (
    <>
      <div className="usage-map">
        {failed ? (
          <p className="muted">The map couldn't load. The list beside it has the same numbers.</p>
        ) : !shapes ? (
          <p className="muted">Loading map…</p>
        ) : (
          <svg
            viewBox={`0 ${shapes.canada.top} 975 ${610 - shapes.canada.top}`}
            role="img"
            aria-label={`Questions by location. ${ranked.join(", ") || "None yet."}`}
          >
            <path d={shapes.canada.d} fill={fill(canada)} className="map-state">
              <title>{`Canada: ${plural(canada)}`}</title>
            </path>
            {canada ? (
              <text x={shapes.canada.label[0]} y={shapes.canada.label[1]} className={`map-n${strong(canada) ? " strong" : ""}`} aria-hidden="true">
                {canada}
              </text>
            ) : null}
            {shapes.states.map((s) => {
              const n = count(`US-${s.code}`);
              return (
                <path key={s.code} d={s.d} fill={fill(n)} className="map-state">
                  <title>{`${s.name}: ${plural(n)}`}</title>
                </path>
              );
            })}
            {shapes.states.map((s) => {
              const n = count(`US-${s.code}`);
              return n && s.label ? (
                <text key={s.code} x={s.label[0]} y={s.label[1]} className={`map-n${strong(n) ? " strong" : ""}`} aria-hidden="true">
                  {n}
                </text>
              ) : null;
            })}
          </svg>
        )}
      </div>
      <div className="map-scale" aria-hidden="true">
        <span>0</span>
        <span className="map-ramp" />
        <span>{max}</span>
      </div>
      <p className="legend muted">
        Hover a state for its count.
        {notRecorded ? ` ${plural(notRecorded)} from other US states came before states were recorded (Oct 9, 2026).` : ""}
        {unknown ? ` ${plural(unknown)} from people who hadn't said where they live.` : ""}
      </p>
    </>
  );
}

// The busiest places, for beside the map.
export function TopPlaces({ places, limit = 10 }: { places: Record<string, number>; limit?: number }) {
  const rows = Object.entries(places)
    .filter(([k]) => k === "CANADA" || /^US-[A-Z]{2}$/.test(k))
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit);
  if (!rows.length) return <p className="empty">No questions with a location in this period.</p>;
  return (
    <table>
      <thead>
        <tr>
          <th scope="col">Location</th>
          <th scope="col" className="right">
            Questions
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([k, n]) => (
          <tr key={k}>
            <td>{k === "CANADA" ? "Canada" : (US_STATES[k.slice(3)] ?? k)}</td>
            <td className="right">{n}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
